import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, ArrowLeft, Check } from 'lucide-react';
import { authErrorMessage } from '../lib/auth-errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing or invalid reset link.');
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(authErrorMessage(data.detail, 'Reset failed.'));
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/advertiser-login'), 2500);
    } catch (err) {
      console.error('reset-password failed:', err);
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-light flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <Link to="/advertiser-login" className="inline-flex items-center gap-2 text-navy/70 hover:text-navy text-sm mb-5" data-testid="reset-password-back">
          <ArrowLeft size={16} /> Back to login
        </Link>
        <h1 className="text-3xl font-bold text-navy mb-2" data-testid="reset-password-title">Set a new password</h1>
        {done ? (
          <div className="text-center py-6" data-testid="reset-password-done">
            <Check className="mx-auto text-emerald-600 mb-2" size={42} />
            <p className="text-emerald-700 font-bold">Password updated.</p>
            <p className="text-navy/70 text-sm mt-1">Redirecting you to login…</p>
          </div>
        ) : (
          <>
            <p className="text-navy/70 text-sm mb-6">Choose a new password (minimum 8 characters).</p>
            <form onSubmit={onSubmit} className="space-y-3" data-testid="reset-password-form">
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-navy/40" size={18} />
                <input
                  type="password" required value={pw}
                  autoComplete="new-password" aria-label="New password"
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="New password"
                  className="w-full pl-10 pr-3 py-3 rounded-lg border border-navy/15 focus:border-navy focus:outline-none"
                  data-testid="reset-password-new-input"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-navy/40" size={18} />
                <input
                  type="password" required value={confirm}
                  autoComplete="new-password" aria-label="Confirm new password"
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full pl-10 pr-3 py-3 rounded-lg border border-navy/15 focus:border-navy focus:outline-none"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              {error && <p className="text-red-600 text-sm" data-testid="reset-password-error">{error}</p>}
              <button
                type="submit"
                disabled={submitting || !token || !pw || !confirm}
                className={`w-full py-3 rounded-full font-bold text-sm ${
                  submitting || !token || !pw || !confirm ? 'bg-navy/40 text-cream/70 cursor-not-allowed' : 'bg-navy text-cream hover:bg-navy-dark'
                }`}
                data-testid="reset-password-submit-btn"
              >
                {submitting ? 'Saving…' : 'Update password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
